import {
  createChannelConfiguration,
  deleteChannelConfiguration,
  updateChannelConfiguration,
} from '@connect-x-app/hybrid-data-access';
import {
  ApiError,
  ChannelConfiguration,
  CreateChannelConfigurationRequest,
  QueryKeys,
} from '@connect-x-app/shared-domain-models';
import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';

export const useCreateChannelConfiguration = (
  options?: UseMutationOptions<ChannelConfiguration, ApiError, CreateChannelConfigurationRequest, unknown>,
) => {
  const queryClient = useQueryClient();

  return useMutation<ChannelConfiguration, ApiError, CreateChannelConfigurationRequest, unknown>({
    ...options,
    mutationFn: (variables: CreateChannelConfigurationRequest) => createChannelConfiguration(variables),
    onSuccess: async (data, variables, context, mutation) => {
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.CHANNELS] });
      options?.onSuccess?.(data, variables, context, mutation);
    },
  });
};

export const useUpdateChannelConfiguration = (
  options?: UseMutationOptions<ChannelConfiguration, ApiError, ChannelConfiguration, unknown>,
) => {
  const queryClient = useQueryClient();

  return useMutation<ChannelConfiguration, ApiError, ChannelConfiguration, unknown>({
    ...options,
    mutationFn: (variables: ChannelConfiguration) => updateChannelConfiguration(variables),
    onSuccess: async (data, variables, context, mutation) => {
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.CHANNELS] });
      options?.onSuccess?.(data, variables, context, mutation);
    },
  });
};

export const useDeleteChannelConfiguration = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation<void, ApiError, string, unknown>({
    mutationFn: (id: string) => deleteChannelConfiguration(id),
    mutationKey: [QueryKeys.CHANNELS],
  });

  const deleteSelectedChannels = async (ids: string[]) => {
    const promises = ids.map(id => mutation.mutateAsync(id));
    try {
      return await Promise.all(promises);
    } finally {
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.CHANNELS] });
    }
  };

  return { ...mutation, deleteSelectedChannels };
};
